import { useRouter } from "next/router";
import { Maybe } from "purify-ts";
import { useMemo } from "react";
import { mightBeEmpty } from "../utils/maybeFromNullable";
import { tryToParseInt } from "../utils/tryToParseInt";
import { isValidRouterNumber } from "./is-valid-router-number";
import { isValidRouterString } from "./is-valid-router-string";
import {
  MaybeRouterVariableObject,
  RouterNumberData,
  RouterStringData,
  RouterVariableObject,
  RouterVariableTypes,
} from "./types";

export function useMaybeRouterVariables<Types extends RouterVariableTypes>(
  types: Types,
): MaybeRouterVariableObject<Types> {
  const { query } = useRouter();

  const routerVariables: MaybeRouterVariableObject<Types> = useMemo(() => {
    let result: Record<string, Maybe<string> | Maybe<number>> = {};

    for (let [key, type] of Object.entries(types)) {
      let v = query[key] as string;

      if (type === "number") {
        const value = tryToParseInt(v as string);
        result[key] = value;
      } else {
        result[key] = mightBeEmpty(v);
      }
    }

    return result as MaybeRouterVariableObject<Types>;
  }, [query, types]);

  return routerVariables;
}

export function useRouterVariables<Types extends RouterVariableTypes>(
  types: Types,
): RouterVariableObject<Types> {
  const { query } = useRouter();

  const routerVariables: RouterVariableObject<Types> = useMemo(() => {
    let result: Record<string, RouterStringData | RouterNumberData> = {};

    for (let [key, type] of Object.entries(types)) {
      let v = query[key] as string;

      if (type === "number") {
        const value = parseInt(v as string, 10);
        result[key] = {
          value: value,
          isInvalid: !isValidRouterNumber(value),
        };
      } else {
        result[key] = {
          value: v,
          isInvalid: !isValidRouterString(v),
        };
      }
    }

    return result as RouterVariableObject<Types>;
  }, [query, types]);

  return routerVariables;
}

export function maybeHasAllValidRouterVariables<
  Types extends RouterVariableTypes,
>(routerVariables: MaybeRouterVariableObject<Types>): Maybe<boolean> {
  const values = Object.values(routerVariables);
  return Maybe.sequence(values).map((vs) => vs.length > 0);
}

export function hasAllValidRouterVariables<Types extends RouterVariableTypes>(
  routerVariables: RouterVariableObject<Types>,
): boolean {
  for (let k of Object.keys(routerVariables)) {
    const v = routerVariables[k];
    if (v.isInvalid) return false;
  }
  return true;
}
